/**
 * Pure mappers between the two scorecard shapes the step straddles:
 *   - Wizard payload  (`payloadRef.current.scorecard` + API hydrate shape)
 *   - ScorecardTemplateBuilder form values (what the builder renders)
 *
 * Kept side-effect-free so tests can exercise the round-trips without
 * React / kula-ai dependencies — the component only composes these.
 */
import type {
  ScorecardItem,
  ScorecardTemplateFormValues,
} from '@kula-ai/components';

interface ApiTemplate {
  name: string;
  questions?: Array<{
    type?: string;
    question?: string;
    description?: string;
    options?: string[];
  }>;
  feedback_description?: string;
  is_feedback_mandatory?: boolean;
  rating_description?: string;
}

/** Map an ATS scorecard template row into the builder's form values. */
export function templateToFormValues(
  template: ApiTemplate | undefined
): ScorecardTemplateFormValues {
  if (!template) {
    return {
      scoreCards: [],
      feedback_description: '',
      is_feedback_mandatory: false,
      rating_description: '',
    };
  }

  const scoreCards: ScorecardItem[] = (template.questions ?? []).map(
    (q, i) => ({
      id: String(i),
      question: q.question ?? '',
      description: q.description,
      type: (q.type as ScorecardItem['type']) ?? 'open_question',
      options: q.options?.map((o) => ({ optionText: o })),
    })
  );

  return {
    scoreCards,
    feedback_description: template.feedback_description ?? '',
    is_feedback_mandatory: Boolean(template.is_feedback_mandatory),
    rating_description: template.rating_description ?? '',
  };
}

/**
 * Map a hydrated `payloadRef.current.scorecard` object (from
 * /ats_interviews/:id, /ats_interviews/new, or a plan child) into
 * the builder's form values. Accepts both camelCase
 * (`feedbackDescription`) and snake_case (`feedback_description`)
 * keys so the scheduling wizard + plan editor share this mapper.
 */
export function payloadToFormValues(
  sc: Record<string, unknown>
): ScorecardTemplateFormValues {
  const questions = Array.isArray(sc.questions) ? sc.questions : [];

  return {
    scoreCards: questions.map((q: Record<string, unknown>, i: number) => ({
      id: String(q.id ?? i),
      question: (q.question as string) ?? '',
      description: q.description as string | undefined,
      type: (q.type as ScorecardItem['type']) ?? 'open_question',
      mandatory: Boolean(q.mandatory),
      options: Array.isArray(q.options)
        ? (q.options as unknown[]).map((o) =>
            typeof o === 'string'
              ? { optionText: o }
              : {
                  optionText:
                    (o as { optionText?: string })?.optionText ?? String(o),
                }
          )
        : undefined,
    })),
    feedback_description:
      (sc.feedbackDescription as string) ??
      (sc.feedback_description as string) ??
      '',
    is_feedback_mandatory: Boolean(sc.is_feedback_mandatory),
    rating_description:
      (sc.ratingDescription as string) ??
      (sc.rating_description as string) ??
      '',
  };
}
